<template>
  <div>
    <section class="panel-card mb-4">
      <div class="panel-heading">
        <div>
          <h2>直接邀请人数排行</h2>
          <p>按当前筛选出的邀请人统计全部已绑定直接关系，最多 10 位；各状态单独列示，不等同于奖励已生效。</p>
        </div>
      </div>
      <div class="table-responsive">
        <table class="table analytics-table mb-0">
          <thead>
            <tr>
              <th>排名</th>
              <th>邀请人</th>
              <th>直接邀请人数</th>
              <th>已达标</th>
              <th>待达标</th>
              <th>已拒绝</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(user, index) in topReferrers" :key="user.id">
              <td><span class="rank-badge">{{ index + 1 }}</span></td>
              <td>
                <strong>{{ user.username }}</strong>
                <button type="button" class="user-id-link" @click="emit('selectUser', user.id)">
                  用户 ID：{{ user.id }}
                </button>
              </td>
              <td>{{ formatNumber(user.referralStats?.invitedCount || 0) }}</td>
              <td>{{ formatNumber(user.referralStats?.qualifiedCount || 0) }}</td>
              <td>{{ formatNumber(user.referralStats?.pendingCount || 0) }}</td>
              <td>{{ formatNumber(user.referralStats?.rejectedCount || 0) }}</td>
            </tr>
            <tr v-if="topReferrers.length === 0">
              <td colspan="6" class="text-center text-muted py-4">当前筛选用户没有直接邀请关系</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="panel-card mb-4">
      <div class="panel-heading">
        <div>
          <h2>邀请关系桑基图</h2>
          <p>从左到右展示直接推荐层次，默认三层；点击可展开节点继续下钻。颜色只表示关系状态，不代表奖励层级。</p>
        </div>
        <span class="dimension-badge">{{ formatNumber(referralRelationships.length) }} 条关系</span>
      </div>
      <ReferralRelationshipGraph
        :relationships="referralRelationships"
        @select-user="emit('selectUser', $event)"
      />
    </section>

    <section class="panel-card mb-4">
      <div class="panel-heading">
        <div>
          <h2>邀请关系明细</h2>
          <p>展示当前筛选邀请人与其直接受邀用户的一跳关系，不推断间接层级或转化率。</p>
        </div>
        <span class="dimension-badge">{{ formatNumber(referralRelationships.length) }} 条关系</span>
      </div>
      <div class="table-responsive relationship-table-wrap">
        <table class="table analytics-table mb-0">
          <thead>
            <tr>
              <th>邀请人</th>
              <th>受邀用户</th>
              <th>关系状态</th>
              <th>绑定时间</th>
              <th>达标时间</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="relationship in referralRelationships" :key="relationship.id">
              <td>
                <strong>{{ relationship.inviter.username }}</strong>
                <button type="button" class="user-id-link" @click="emit('selectUser', relationship.inviterId)">
                  用户 ID：{{ relationship.inviterId }}
                </button>
              </td>
              <td>
                <strong>{{ relationship.invitee?.username || `用户 #${relationship.inviteeId}` }}</strong>
                <button type="button" class="user-id-link" @click="emit('selectUser', relationship.inviteeId)">
                  用户 ID：{{ relationship.inviteeId }}
                </button>
              </td>
              <td>
                <span class="status-badge" :class="`status-badge--${relationship.status}`">
                  {{ referralStatusLabel(relationship.status) }}
                </span>
              </td>
              <td>{{ formatTime(relationship.createdAt) }}</td>
              <td>{{ formatTime(relationship.qualifiedAt) }}</td>
            </tr>
            <tr v-if="referralRelationships.length === 0">
              <td colspan="5" class="text-center text-muted py-4">当前筛选用户没有可分析的邀请关系</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import ReferralRelationshipGraph from '@/components/admin/ReferralRelationshipGraph.vue'
import type { UserWithActivity } from '@/types/user-statistics'
import { buildReferralRelationships, getTopReferrers } from '@/utils/user-statistics'

const props = defineProps<{
  filteredUsers: UserWithActivity[]
  allUsers: UserWithActivity[]
}>()
const emit = defineEmits<{ selectUser: [userId: number] }>()

const topReferrers = computed(() => getTopReferrers(props.filteredUsers))
const referralRelationships = computed(() => buildReferralRelationships(props.filteredUsers, props.allUsers))
const referralStatusLabels: Record<string, string> = {
  pending: '待达标',
  qualified: '已达标',
  rejected: '已拒绝',
}

const formatNumber = (value: number) => new Intl.NumberFormat('zh-CN').format(value)
const formatTime = (value: string | Date | null | undefined) => value
  ? new Date(value).toLocaleString('zh-CN', { hour12: false })
  : '-'
const referralStatusLabel = (status: string) => referralStatusLabels[status] || status
</script>

<style scoped>
.panel-card {
  padding: 1.4rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
}

.panel-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.panel-heading h2 {
  margin: 0;
  font-size: 1.08rem;
  font-weight: 700;
}

.panel-heading p {
  margin: 0.25rem 0 0;
  color: #64748b;
  font-size: 0.82rem;
}

.dimension-badge,
.rank-badge,
.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.28rem 0.55rem;
  font-size: 0.76rem;
  font-weight: 700;
  border-radius: 999px;
}

.dimension-badge {
  color: #0f766e;
  background: #ccfbf1;
}

.analytics-table th {
  color: #475569;
  font-size: 0.78rem;
  text-transform: uppercase;
  background: #f8fafc;
}

.analytics-table td,
.analytics-table th {
  padding: 0.8rem;
  vertical-align: middle;
  border-color: #e2e8f0;
}

.rank-badge {
  min-width: 1.8rem;
  color: #1d4ed8;
  background: #dbeafe;
}

.status-badge--pending {
  color: #a16207;
  background: #fef9c3;
}

.status-badge--qualified {
  color: #047857;
  background: #d1fae5;
}

.status-badge--rejected {
  color: #be123c;
  background: #ffe4e6;
}

.relationship-table-wrap {
  max-height: 32rem;
  overflow: auto;
}

.user-id-link {
  display: block;
  padding: 0;
  color: #2563eb;
  font-size: 0.75rem;
  background: none;
  border: 0;
}

.user-id-link:hover,
.user-id-link:focus-visible {
  text-decoration: underline;
}
</style>
